import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiService from '@/services/api';

interface ServiceCategory {
  id: number;
  name: string;
  slug: string;
}

const Footer = () => {
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchServiceCategories = async () => {
      try {
        const response = await apiService.get('/public/service-categories-with-services');
        if (response.success && Array.isArray(response.data)) {
          setServiceCategories(response.data.slice(0, 4)); // Show first 4 categories in footer
        }
      } catch (error) {
        console.error('Error fetching service categories:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchServiceCategories();
  }, []);

  return (
    <footer className="bg-teal-700 text-teal-100 mt-auto">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Virewire */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">Virewire</h3>
            <p className="text-teal-100 text-sm leading-relaxed mb-6">
              Redefining property management through authority and orchestration.
            </p>
            {/* Social Icons */}
            <div className="flex gap-3">
              <a href="https://facebook.com/virewire" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center hover:bg-teal-500 transition-colors">
                <Facebook className="h-5 w-5 text-white" />
              </a>
              <a href="https://twitter.com/virewire" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center hover:bg-teal-500 transition-colors">
                <Twitter className="h-5 w-5 text-white" />
              </a>
              <a href="https://linkedin.com/company/virewire" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center hover:bg-teal-500 transition-colors">
                <Linkedin className="h-5 w-5 text-white" />
              </a>
              <a href="https://instagram.com/virewire" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center hover:bg-teal-500 transition-colors">
                <Instagram className="h-5 w-5 text-white" />
              </a>
            </div>
          </div>

          {/* Services - Database Driven */}
          <div>
            <h4 className="text-white font-semibold text-base mb-6">Services</h4>
            <ul className="space-y-4">
              {isLoading ? (
                <li className="text-teal-100 text-sm">Loading services...</li>
              ) : serviceCategories.length > 0 ? (
                serviceCategories.map((category) => (
                  <li key={category.id}>
                    <Link 
                      to={`/services#${category.slug}`} 
                      className="text-teal-100 hover:text-white text-sm transition-colors"
                    >
                      {category.name}
                    </Link>
                  </li>
                ))
              ) : (
                <li className="text-teal-100 text-sm">Services unavailable</li>
              )}
              <li>
                <Link to="/services" className="text-teal-100 hover:text-white text-sm transition-colors font-semibold">
                  View All Services →
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold text-base mb-6">Company</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/about" className="text-teal-100 hover:text-white text-sm transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-teal-100 hover:text-white text-sm transition-colors">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/locations" className="text-teal-100 hover:text-white text-sm transition-colors">
                  Locations
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* CTA Strip */}
        <div className="bg-teal-800 rounded-xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          <div>
            <h4 className="text-white font-semibold text-base mb-1">Ready to get a quote?</h4>
            <p className="text-teal-100 text-sm">Get instant quotes for professional property services.</p>
          </div>
          <Button asChild className="bg-lime-500 hover:bg-lime-600 text-slate-900 font-semibold flex-shrink-0">
            <Link to="/services">Request Service</Link>
          </Button>
        </div>

        {/* Divider */}
        <div className="border-t border-teal-600 my-8"></div>

        {/* Copyright & Links */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-teal-100 text-sm">
            © {new Date().getFullYear()} Virewire Managed Services. All rights reserved.
          </p>
          <div className="flex gap-6 flex-wrap">
            <Link to="/provider/login" className="text-teal-100 hover:text-white text-sm transition-colors">
              Provider Login
            </Link>
            <Link to="/admin/login" className="text-teal-100 hover:text-white text-sm transition-colors">
              Admin Login
            </Link>
            <Link to="/privacy" className="text-teal-100 hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-teal-100 hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
            <Link to="/cookies" className="text-teal-100 hover:text-white text-sm transition-colors">
              Cookies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
